import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { SessionState, ParsedScript } from "../types";
import { tokenizeHtml } from "../core/script";
import { measurePromptLines } from "../core/prompt-geometry";
import { TalentCue } from "./TalentCue";
import {
  createAnimationDriver,
  stepMotion,
  type Motion,
} from "../studio/motion";

type WordGeometry = {
  top: number;
  nextTop: number;
  rank: number;
  count: number;
};
export function PromptSurface({
  state,
  parsed,
  onSeek,
  onWordClick,
  talent = false,
}: {
  state: SessionState;
  parsed: ParsedScript;
  onSeek?: (index: number) => void;
  /** Explicit word clicks only; manual scrolling must not focus the editor. */
  onWordClick?: (index: number) => void;
  talent?: boolean;
  compact?: boolean;
}) {
  const viewport = useRef<HTMLDivElement>(null),
    content = useRef<HTMLDivElement>(null),
    surface = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 1024, height: 600 });
  const touchY = useRef<number | null>(null);
  const touchMoved = useRef(false);
  const html = useMemo(
    () => tokenizeHtml(state.script.html),
    [state.script.html],
  );
  const settings = state.settings;
  const canvasWidth = settings.outputWidth || 1024,
    canvasHeight = settings.outputHeight || 600,
    scale = Math.min(size.width / canvasWidth, size.height / canvasHeight);
  const spokenCount = useMemo(
    () => parsed.words.filter((w) => !w.cue).length,
    [parsed],
  );
  const wakeAnimation = useRef<() => void>(() => {});
  const latest = useRef({
    state,
    parsed,
    spokenCount,
    receivedAt: performance.now(),
  });
  // A snapshot supplies a precise fractional position; local interpolation fills the frames between snapshots.
  useLayoutEffect(() => {
    const previous = latest.current.state;
    latest.current = {
      state,
      parsed,
      spokenCount,
      receivedAt: performance.now(),
    };
    // Status, elapsed time and unrelated controls must not wake a resting surface.
    if (
      previous.wordIndex !== state.wordIndex ||
      previous.wordProgress !== state.wordProgress ||
      previous.playing !== state.playing ||
      previous.script.id !== state.script.id ||
      previous.settings.mode !== state.settings.mode ||
      previous.settings.wpm !== state.settings.wpm ||
      previous.settings.duration !== state.settings.duration ||
      previous.settings.readingLine !== state.settings.readingLine ||
      previous.settings.smoothScroll !== state.settings.smoothScroll
    )
      wakeAnimation.current();
  }, [state, parsed, spokenCount]);
  const geometry = useRef<Map<number, WordGeometry>>(new Map());
  const wordNodes = useRef<Map<number, HTMLElement[]>>(new Map());
  const previousWord = useRef<number | null>(null);
  const scrollInvalidated = useRef(true);
  useLayoutEffect(() => {
    initialized.current = false;
    scrollInvalidated.current = true;
  }, [state.script.id]);
  const motion = useRef<Motion>({ position: 0, velocity: 0 });
  const initialized = useRef(false);
  useLayoutEffect(() => {
    const root = content.current;
    if (!root) return;
    let measuredWidth = -1,
      measuredHeight = -1;
    const measure = (force = false) => {
      const width = root.clientWidth,
        height = root.clientHeight;
      if (!force && width === measuredWidth && height === measuredHeight)
        return;
      measuredWidth = width;
      measuredHeight = height;
      const { lines, nodes } = measurePromptLines(root);
      const map = new Map<number, WordGeometry>();
      let nextTop: number | undefined;
      for (let lineIndex = lines.length - 1; lineIndex >= 0; lineIndex--) {
        const line = lines[lineIndex],
          spoken = line.indexes.filter((index) => !parsed.words[index]?.cue);
        let rank = 0;
        line.indexes.forEach((index) => {
          map.set(index, {
            top: line.top,
            nextTop: nextTop ?? line.top,
            rank: parsed.words[index]?.cue ? 0 : rank++,
            count: Math.max(1, spoken.length),
          });
        });
        if (spoken.length) nextTop = line.top;
      }
      geometry.current = map;
      wordNodes.current = nodes;
      scrollInvalidated.current = true;
      wakeAnimation.current();
    };
    measure(true);
    const observer = new ResizeObserver(() => measure());
    observer.observe(root);
    // A font substitution can redistribute words without changing total content height.
    const fontsChanged = () => measure(true);
    document.fonts?.addEventListener("loadingdone", fontsChanged);
    // WebKit can finish an already-loading font without emitting loadingdone.
    // Its new glyph metrics can move a line while total content dimensions stay equal.
    let live = true;
    if (document.fonts?.status === "loading")
      void document.fonts.ready.then(() => {
        if (live) measure(true);
      });
    return () => {
      live = false;
      observer.disconnect();
      document.fonts?.removeEventListener("loadingdone", fontsChanged);
    };
  }, [
    html,
    settings.fontSize,
    settings.fontFamily,
    settings.lineHeight,
    settings.margin,
    settings.readingLine,
    settings.direction,
    settings.alignment,
    canvasWidth,
    canvasHeight,
    parsed,
  ]);
  useLayoutEffect(() => {
    previousWord.current = null;
  }, [html, settings.highlightCurrent]);
  useLayoutEffect(() => {
    const before = previousWord.current;
    const apply = (index: number) =>
      wordNodes.current.get(index)?.forEach((el) => {
        el.classList.toggle(
          "active-word",
          settings.highlightCurrent && index === state.wordIndex,
        );
        el.classList.toggle("past-word", index < state.wordIndex);
      });
    if (before === null) {
      for (const index of wordNodes.current.keys()) apply(index);
    } else {
      for (
        let index = Math.min(before, state.wordIndex);
        index <= Math.max(before, state.wordIndex);
        index++
      )
        apply(index);
    }
    previousWord.current = state.wordIndex;
  }, [state.wordIndex, html, settings.highlightCurrent]);
  useEffect(() => {
    let lastWrittenPosition: number | undefined;
    const driver = createAnimationDriver(
      (now, dt) => {
        const box = viewport.current;
        const snapshot = latest.current;
        const s = snapshot.state,
          g = geometry.current.get(s.wordIndex);
        if (box && g) {
          const automatic =
            s.playing &&
            (s.settings.mode === "constant" || s.settings.mode === "timed");
          const rate =
            s.settings.mode === "timed"
              ? snapshot.spokenCount / Math.max(1, s.settings.duration)
              : s.settings.wpm / 60;
          const progress = automatic
            ? Math.min(
                0.999,
                (s.wordProgress || 0) +
                  Math.min(0.15, (now - snapshot.receivedAt) / 1000) * rate,
              )
            : s.wordProgress || 0;
          const continuous =
            s.settings.mode === "constant" ||
            s.settings.mode === "timed" ||
            s.settings.mode === "sound";
          const fraction = continuous ? (g.rank + progress) / g.count : 0;
          const desired = Math.max(
            0,
            g.top +
              (g.nextTop - g.top) * fraction -
              ((s.settings.outputHeight || 600) * s.settings.readingLine) / 100,
          );
          if (!initialized.current) {
            motion.current = { position: desired, velocity: 0 };
            initialized.current = true;
          } else
            motion.current = stepMotion(
              motion.current,
              desired,
              dt,
              s.settings.smoothScroll ? 0.24 : 0.09,
            );
          // A browser can quantize scrollTop; keep the unrounded model for the next frame.
          if (
            scrollInvalidated.current ||
            lastWrittenPosition !== motion.current.position
          ) {
            box.scrollTop = motion.current.position;
            lastWrittenPosition = motion.current.position;
            scrollInvalidated.current = false;
          }
          const interpolating =
            automatic &&
            now - snapshot.receivedAt < 150 &&
            progress < 0.999 &&
            g.nextTop > g.top;
          return (
            interpolating ||
            motion.current.position !== desired ||
            motion.current.velocity !== 0
          );
        }
        return false;
      },
      {
        now: () => performance.now(),
        request: (callback) => requestAnimationFrame(callback),
        cancel: (handle) => cancelAnimationFrame(handle),
      },
    );
    wakeAnimation.current = driver.wake;
    const visibility = () => {
      driver.setSuspended(document.visibilityState === "hidden");
    };
    document.addEventListener("visibilitychange", visibility);
    visibility();
    return () => {
      wakeAnimation.current = () => {};
      document.removeEventListener("visibilitychange", visibility);
      driver.dispose();
    };
  }, []);
  useEffect(() => {
    const el = surface.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0].contentRect;
      setSize({ width: rect.width, height: rect.height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const currentSeek = useRef(onSeek);
  currentSeek.current = onSeek;
  useEffect(() => {
    const el = viewport.current;
    if (!el) return;
    const wheel = (e: WheelEvent) => {
      e.preventDefault();
      const snapshot = latest.current;
      if (currentSeek.current && e.deltaY)
        currentSeek.current(
          Math.max(
            0,
            Math.min(
              snapshot.parsed.words.length - 1,
              snapshot.state.wordIndex +
                Math.sign(e.deltaY) *
                  Math.max(1, Math.ceil(Math.abs(e.deltaY) / 30)),
            ),
          ),
        );
    };
    el.addEventListener("wheel", wheel, { passive: false });
    return () => el.removeEventListener("wheel", wheel);
  }, []);
  return (
    <div
      ref={surface}
      className={`prompt-surface ${talent ? "talent-surface" : ""} ${settings.showPast ? "" : "hide-past"}`}
      style={{ background: settings.background, color: settings.foreground }}
    >
      {!parsed.words.length && (
        <div className="empty-prompt">Your script will appear here.</div>
      )}
      <div
        className="prompt-transform"
        style={{
          width: canvasWidth,
          height: canvasHeight,
          left: (size.width - canvasWidth * scale) / 2,
          top: (size.height - canvasHeight * scale) / 2,
          transformOrigin: "top left",
          transform: `scale(${scale})`,
        }}
      >
        <div className="prompt-optical">
          {settings.showGuide && (
            <div
              className="reading-guide"
              style={{
                top: `${settings.readingLine}%`,
                opacity: settings.guideOpacity,
              }}
            >
              <span />
              <span />
            </div>
          )}
          <div
            className="prompt-viewport"
            ref={viewport}
            tabIndex={onSeek ? 0 : -1}
            role="region"
            aria-label={
              talent ? "Teleprompter script" : "Live teleprompter preview"
            }
            onKeyDown={(event) => {
              if (event.key !== "PageDown" && event.key !== "PageUp") return;
              // Native viewport scrolling would move only this window, outside the room state.
              event.preventDefault();
              if (!onSeek) return;
              const snapshot = latest.current;
              const current = geometry.current.get(snapshot.state.wordIndex);
              if (!current) return;
              const direction = event.key === "PageDown" ? 1 : -1;
              const destination = current.top + direction * canvasHeight * 0.8;
              let candidate = snapshot.state.wordIndex,
                distance = Infinity;
              for (const [index, word] of geometry.current) {
                if (
                  snapshot.parsed.words[index]?.cue ||
                  (index - snapshot.state.wordIndex) * direction <= 0
                )
                  continue;
                const delta = Math.abs(word.top - destination);
                if (
                  delta < distance ||
                  (delta === distance && index < candidate)
                ) {
                  candidate = index;
                  distance = delta;
                }
              }
              onSeek(candidate);
            }}
            onPointerDown={(e) => {
              if (e.pointerType === "touch") {
                touchY.current = e.clientY;
                touchMoved.current = false;
                e.currentTarget.setPointerCapture(e.pointerId);
              }
            }}
            onPointerMove={(e) => {
              if (
                touchY.current !== null &&
                onSeek &&
                Math.abs(e.clientY - touchY.current) > 18
              ) {
                const delta = e.clientY - touchY.current;
                touchMoved.current = true;
                onSeek(
                  Math.max(
                    0,
                    Math.min(
                      parsed.words.length - 1,
                      state.wordIndex - Math.sign(delta) * 3,
                    ),
                  ),
                );
                touchY.current = e.clientY;
              }
            }}
            onPointerUp={() => {
              touchY.current = null;
            }}
            onPointerCancel={() => {
              touchY.current = null;
            }}
          >
            <div
              ref={content}
              className="prompt-content"
              dir={settings.direction}
              style={{
                fontSize: `${settings.fontSize}px`,
                lineHeight: settings.lineHeight,
                fontFamily: settings.fontFamily,
                textAlign: settings.alignment,
                padding: `${(canvasHeight * settings.readingLine) / 100}px ${settings.margin}% ${canvasHeight}px`,
              }}
              onClick={(e) => {
                if (touchMoved.current) {
                  touchMoved.current = false;
                  return;
                }
                const target = (e.target as HTMLElement).closest<HTMLElement>(
                  "[data-word-index]",
                );
                if (target && onSeek) {
                  const wordIndex = Number(target.dataset.wordIndex);
                  onSeek(wordIndex);
                  onWordClick?.(wordIndex);
                }
              }}
              dangerouslySetInnerHTML={{ __html: html }}
            />
          </div>
          {state.visualCue && !state.blackout && !state.emergency && (
            <TalentCue cue={state.visualCue} width={canvasWidth} height={canvasHeight}
              opacity={settings.cueOpacity} layer={settings.cueLayer} />
          )}
        </div>
      </div>
      {state.blackout && (
        <div className="blackout-overlay" aria-label="Output blacked out" />
      )}
      {state.emergency && (
        <div className="emergency-overlay" role="alert">
          {state.emergency}
        </div>
      )}
      {state.countdownUntil && state.countdownUntil > Date.now() && (
        <Countdown until={state.countdownUntil} />
      )}
    </div>
  );
}
function Countdown({ until }: { until: number }) {
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const tick = () => {
      if (ref.current)
        ref.current.textContent = String(
          Math.max(1, Math.ceil((until - Date.now()) / 1000)),
        );
    };
    tick();
    const interval = setInterval(tick, 100);
    return () => clearInterval(interval);
  }, [until]);
  return <div className="countdown-overlay"><span className="countdown-help" ref={ref} data-help="Seconds remaining before prompting begins." tabIndex={0} /></div>;
}
